import axios, { AxiosInstance } from 'axios';
import { config } from '../config';
import { User, Skill, Submission, Review } from '../types';

class ApiService {
    private api: AxiosInstance;
    private token: string | null = null;

    constructor() {
        this.api = axios.create({
            baseURL: config.apiBaseUrl,
            headers: {
                'Content-Type': 'application/json',
            },
        });

        // Load token from localStorage
        this.token = localStorage.getItem('authToken');
        if (this.token) {
            this.setAuthToken(this.token);
        }

        // Add response interceptor for error handling
        this.api.interceptors.response.use(
            (response) => {
                this.hideLoader();
                return response;
            },
            (error) => {
                this.hideLoader();
                if (error.response?.status === 401) {
                    // Token expired or invalid
                    this.clearAuthToken();
                    window.location.href = '/login';
                }
                return Promise.reject(error);
            }
        );

        // Add request interceptor for loader
        this.api.interceptors.request.use(
            (config) => {
                this.showLoader();
                return config;
            },
            (error) => {
                this.hideLoader();
                return Promise.reject(error);
            }
        );
    }

    private setAuthToken(token: string) {
        this.token = token;
        this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        localStorage.setItem('authToken', token);
    }

    private clearAuthToken() {
        this.token = null;
        delete this.api.defaults.headers.common['Authorization'];
        localStorage.removeItem('authToken');
    }

    // Loader Callbacks
    private onShowLoader: () => void = () => { };
    private onHideLoader: () => void = () => { };
    private requestCount = 0;

    public setLoaderCallbacks(show: () => void, hide: () => void) {
        this.onShowLoader = show;
        this.onHideLoader = hide;
    }

    private showLoader() {
        if (this.requestCount === 0) {
            this.onShowLoader();
        }
        this.requestCount++;
    }

    private hideLoader() {
        this.requestCount--;
        if (this.requestCount <= 0) {
            this.requestCount = 0;
            this.onHideLoader();
        }
    }

    // Authentication
    async login(email: string, password: string): Promise<{ token: string; user: User }> {
        const response = await this.api.post('/auth/login', { email, password });
        const { token, user } = response.data;

        // Convert user ID fields to string for frontend compatibility
        const formattedUser: User = {
            id: user.id.toString(),
            name: user.name,
            email: user.email,
            role: user.role,
            managerId: user.managerId?.toString(),
            departmentName: user.departmentName,
            divisionName: user.divisionName,
            isActive: user.isActive
        };

        this.setAuthToken(token);
        return { token, user: formattedUser };
    }

    logout() {
        this.clearAuthToken();
    }

    async hasTeamMembers(): Promise<boolean> {
        try {
            const response = await this.api.get('/users/has-team');
            return response.data.hasTeam;
        } catch (error) {
            return false;
        }
    }

    async getEmployeeHierarchy(): Promise<User[]> {
        try {
            const response = await this.api.get('/users/hierarchy');
            return response.data.map((user: any) => ({
                id: user.id.toString(),
                name: user.name,
                email: user.email,
                role: user.role,
                managerId: user.managerId?.toString(),
                departmentName: user.departmentName,
                divisionName: user.divisionName,
                isActive: user.isActive
            }));
        } catch (error) {
            return [];
        }
    }

    async changePassword(oldPassword: string, newPassword: string): Promise<void> {
        await this.api.post('/auth/change-password', {
            oldPassword,
            newPassword
        });
    }

    async forgotPassword(email: string): Promise<void> {
        await this.api.post('/auth/forgot-password', { email });
    }

    async verifyOtp(email: string, otp: string): Promise<void> {
        await this.api.post('/auth/verify-otp', { email, otp });
    }

    async resetPassword(email: string, otp: string, newPassword: string): Promise<void> {
        await this.api.post('/auth/reset-password', { email, otp, newPassword });
    }

    // Skills
    async getSkills(): Promise<Skill[]> {
        const response = await this.api.get('/skills');
        return response.data.map((skill: any) => ({
            id: skill.id.toString(),
            name: skill.name,
            category: skill.category,
            isActive: skill.isActive
        }));
    }

    // Submissions
    async getMySubmissions(): Promise<Submission[]> {
        const response = await this.api.get('/submissions');
        return response.data.map((sub: any) => ({
            id: sub.id.toString(),
            engineerId: sub.engineerId.toString(),
            engineerName: sub.engineerName,
            periodType: sub.periodType,
            period: sub.period,
            status: sub.status,
            details: sub.details.map((d: any) => ({
                skillId: d.skillId.toString(),
                selfScore: d.selfScore,
                achievementNotes: d.achievementNotes,
                certificates: d.certificates || [],
            })),
            createdAt: sub.createdAt,
            updatedAt: sub.updatedAt,
            skillCount: sub.skillCount,
            certificateCount: sub.certificateCount,
        }));
    }

    async getSubmissionsByEmployee(employeeId: string): Promise<Submission[]> {
        const response = await this.api.get(`/submissions/employee/${employeeId}`);
        return response.data.map((sub: any) => ({
            id: sub.id.toString(),
            engineerId: sub.engineerId.toString(),
            engineerName: sub.engineerName,
            periodType: sub.periodType,
            period: sub.period,
            status: sub.status,
            details: sub.details.map((d: any) => ({
                skillId: d.skillId.toString(),
                selfScore: d.selfScore,
                achievementNotes: d.achievementNotes,
                certificates: d.certificates || [],
            })),
            createdAt: sub.createdAt,
            updatedAt: sub.updatedAt,
            skillCount: sub.skillCount,
            certificateCount: sub.certificateCount,
        }));
    }

    async getSubmissionById(id: string): Promise<Submission | null> {
        try {
            const response = await this.api.get(`/submissions/${id}`);
            const sub = response.data;
            return {
                id: sub.id.toString(),
                engineerId: sub.engineerId.toString(),
                engineerName: sub.engineerName,
                periodType: sub.periodType,
                period: sub.period,
                status: sub.status,
                details: sub.details.map((d: any) => ({
                    skillId: d.skillId.toString(),
                    selfScore: d.selfScore,
                    achievementNotes: d.achievementNotes,
                    certificates: d.certificates || [],
                })),
                createdAt: sub.createdAt,
                updatedAt: sub.updatedAt,
            };
        } catch (error) {
            return null;
        }
    }

    async createSubmission(submission: Omit<Submission, 'id' | 'createdAt' | 'updatedAt'>): Promise<void> {
        await this.api.post('/submissions', submission);
    }

    async updateSubmission(id: string, submission: Omit<Submission, 'id' | 'createdAt' | 'updatedAt'>): Promise<void> {
        await this.api.put(`/submissions/${id}`, submission);
    }

    async deleteSubmission(id: string): Promise<void> {
        await this.api.delete(`/submissions/${id}`);
    }

    // Reviews
    async getTeamSubmissions(): Promise<Submission[]> {
        const response = await this.api.get('/reviews/team');
        return response.data.map((sub: any) => ({
            id: sub.id.toString(),
            engineerId: sub.engineerId.toString(),
            engineerName: sub.engineerName,
            periodType: sub.periodType,
            period: sub.period,
            status: sub.status,
            details: sub.details || [],
            createdAt: sub.createdAt,
            updatedAt: sub.updatedAt,
            skillCount: sub.skillCount,
            certificateCount: sub.certificateCount,
        }));
    }

    async getReviewBySubmissionId(submissionId: string): Promise<Review | null> {
        try {
            const response = await this.api.get(`/reviews/submission/${submissionId}`);
            const rev = response.data;
            return {
                id: rev.id.toString(),
                submissionId: rev.submissionId.toString(),
                managerId: rev.managerId.toString(),
                status: rev.status,
                overallComments: rev.overallComments,
                details: rev.details.map((d: any) => ({
                    skillId: d.skillId.toString(),
                    managerScore: d.managerScore,
                    managerComment: d.managerComment,
                })),
                createdAt: rev.createdAt,
                updatedAt: rev.updatedAt,
            };
        } catch (error) {
            return null;
        }
    }

    async createReview(review: Omit<Review, 'id' | 'createdAt' | 'updatedAt'>): Promise<void> {
        await this.api.post('/reviews', review);
    }

    async updateReview(id: string, review: Omit<Review, 'id' | 'createdAt' | 'updatedAt'>): Promise<void> {
        await this.api.put(`/reviews/${id}`, review);
    }

    // Reports (using existing endpoints)
    async getReportsData(userId: string, role: string): Promise<{ submissions: Submission[]; reviews: Review[] }> {
        if (role === 'Manager') {
            const submissions = await this.getTeamSubmissions();
            const reviews: Review[] = [];

            // Get reviews for each submission
            for (const sub of submissions) {
                const review = await this.getReviewBySubmissionId(sub.id);
                if (review) {
                    reviews.push(review);
                }
            }

            return { submissions, reviews };
        } else {
            const submissions = await this.getMySubmissions();
            const reviews: Review[] = [];

            // Get reviews for each submission
            for (const sub of submissions) {
                const review = await this.getReviewBySubmissionId(sub.id);
                if (review) {
                    reviews.push(review);
                }
            }

            return { submissions, reviews };
        }
    }
}

export const apiService = new ApiService();
